"use client";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";
import { Modal } from "@gettactic/components/src/overlay/modals/Modal";
import { Button } from "@cmp-src/primitives/button";
import { HookFormOfficeSelectorInput } from "@gettactic/components/src/form/OfficeSelectorInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { SelectItem } from "@cmp-src/form/UnifiedSelect";
import { Stack } from "@cmp-src/primitives";

const invitationLinkSchema = z.object({
  office_id: z.string().min(1, "Office is required"),
  station_id: z.string().min(1, "Station is required")
});

type InvitationLinkFormValues = z.infer<typeof invitationLinkSchema>;

interface InvitationLinkModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InvitationLinkModal: React.FC<InvitationLinkModalProps> = ({
  isOpen,
  onOpenChange
}) => {
  const [isCopying, setIsCopying] = useState(false);

  const form = useForm<InvitationLinkFormValues>({
    resolver: zodResolver(invitationLinkSchema),
    defaultValues: {
      office_id: "",
      station_id: ""
    }
  });

  const selectedOfficeId = form.watch("office_id");

  // Fetch stations for the selected office
  const { data: stationsData, isLoading: isLoadingStations } = useQuery({
    queryKey: ["visitorStations", "byOffice", selectedOfficeId],
    queryFn: async () => {
      const response = await api.client.visitorStations.list({
        office_id: selectedOfficeId,
        type: "office"
      });
      return response.result;
    },
    enabled: isOpen && !!selectedOfficeId
  });

  // Build station items for selected office
  const stationItems: SelectItem[] = useMemo(() => {
    if (!stationsData?.elements || !selectedOfficeId) return [];
    return stationsData.elements.map((station) => ({
      id: station.id,
      name: station.name
    }));
  }, [stationsData, selectedOfficeId]);

  // Reset station when office changes
  useEffect(() => {
    form.setValue("station_id", "");
  }, [selectedOfficeId, form]);

  // Generate and copy the invitation link
  const handleCopyLink = useCallback(
    async (values: InvitationLinkFormValues) => {
      if (isCopying) return;

      setIsCopying(true);
      try {
        const response = await api.client.visits.createInvitation();
        if (response.ok && response.result?.code) {
          const url = new URL(
            "/public/visitor/invitation",
            window.location.origin
          );
          url.searchParams.set("code", response.result.code);
          url.searchParams.set("office_id", values.office_id);
          url.searchParams.set("station_id", values.station_id);

          await navigator.clipboard.writeText(url.toString());
          toast.success("Pre-registration link copied to clipboard");
          onOpenChange(false);
          setTimeout(() => form.reset(), 200);
        } else {
          toast.error("Failed to generate pre-registration link");
        }
      } catch {
        toast.error("Failed to copy pre-registration link");
      } finally {
        setIsCopying(false);
      }
    },
    [isCopying, onOpenChange, form]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(() => {
      form.reset();
    }, 200);
  }, [onOpenChange, form]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOpenChange={onOpenChange}
      title="Copy Pre-Registration Link"
      description={
        <>
          Select an office and station to generate a pre-registration link for
          visitors.
        </>
      }
      size="sm"
    >
      <Stack>
        <form onSubmit={form.handleSubmit(handleCopyLink)}>
          <div className="flex flex-col gap-3">
            <HookFormOfficeSelectorInput
              form={form}
              name="office_id"
              label="Office"
              isRequired
            />

            <HookFormUnifiedSelectInput
              form={form}
              name="station_id"
              label="Station"
              items={stationItems}
              placeholder={
                !selectedOfficeId
                  ? "Select an office first"
                  : isLoadingStations
                    ? "Loading stations..."
                    : "Select a station"
              }
              disabled={!selectedOfficeId || isLoadingStations}
              isRequired
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCopying} className="gap-2">
              {isCopying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link className="h-4 w-4" />
              )}
              Copy Link
            </Button>
          </div>
        </form>
      </Stack>
    </Modal>
  );
};
